import React from 'react';
import styled from 'styled-components';
import { FaAward, FaExternalLinkAlt } from 'react-icons/fa';

const certifications = [
  {
    id: 1,
    title: 'Databricks Certified Generative AI Fundamentals',
    issuer: 'Databricks',
    date: '04/2024 - 04/2026',
    credentialId: 'd21683c2-9dd8-41cc-b061-815f245f96e7',
    credentialUrl: 'https://credentials.databricks.com/d21683c2-9dd8-41cc-b061-815f245f96e7#acc.kS197hYt',
    skills: ['Generative AI', 'LLMs', 'Databricks', 'AI Applications', 'Prompt Engineering']
  },
  {
    id: 2,
    title: 'Databricks Certified Data Engineer Associate',
    issuer: 'Databricks',
    date: '09/2024 - 09/2026',
    credentialId: 'e08c2e4b-bc3f-4f05-95bf-280c2e494e2e',
    credentialUrl: 'https://credentials.databricks.com/e08c2e4b-bc3f-4f05-95bf-280c2e494e2e#acc.2PxCOKBw',
    skills: ['Databricks', 'Apache Spark', 'Delta Lake', 'ETL', 'Big Data']
  },
  {
    id: 3,
    title: 'Big Data on AWS',
    issuer: 'Udemy',
    date: 'Issued October 2022',
    credentialId: 'UC-afb277f7-4bf6-4c12-bccb-0856cdf4a763',
    credentialUrl: 'https://www.udemy.com/certificate/UC-afb277f7-4bf6-4c12-bccb-0856cdf4a763/',
    skills: ['AWS', 'Big Data', 'Cloud Computing', 'Data Processing', 'ETL']
  },
  {
    id: 4,
    title: 'Deep Learning Specialization',
    issuer: 'deeplearning.ai (Coursera)',
    date: 'Issued June 2020',
    credentialId: 'NDF5HXKVET77',
    credentialUrl: 'https://www.coursera.org/account/accomplishments/specialization/NDF5HXKVET77',
    skills: ['Deep Learning', 'Neural Networks', 'CNN', 'RNN', 'TensorFlow', 'Keras']
  },
  {
    id: 5,
    title: 'Microsoft Technology Associate: Introduction to Programming using Python',
    issuer: 'Microsoft',
    date: 'Issued October 2018',
    credentialId: '0899e2f4-30aa-4c5a-aad7-6be0d9e27132',
    credentialUrl: 'https://www.credly.com/badges/0899e2f4-30aa-4c5a-aad7-6be0d9e27132/linked_in_profile',
    skills: ['Python', 'Programming Fundamentals', 'Algorithm Design', 'Problem Solving']
  }
];

const Certifications = () => {
  return (
    <CertificationsWrapper id="certifications">
      <div className="container">
        <h2 className="section-title">My Certifications</h2>
        
        <div className="certifications-grid">
          {certifications.map((cert) => (
            <div className="certification-card" key={cert.id}>
              <div className="certification-icon">
                <FaAward />
              </div>
              <div className="certification-content">
                <h3>{cert.title}</h3>
                <div className="certification-meta">
                  <span>{cert.issuer}</span>
                  <span>{cert.date}</span>
                </div>
                <div className="skills">
                  {cert.skills.map((skill, index) => (
                    <span key={index} className="skill-tag">{skill}</span>
                  ))}
                </div>
                <a 
                  href={cert.credentialUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="view-credential"
                >
                  View Credential <FaExternalLinkAlt className="external-icon" />
                </a>
              </div>
            </div>
          ))}
        </div>
      </div>
    </CertificationsWrapper>
  );
};

const CertificationsWrapper = styled.section`
  background-color: #f8fafc;
  padding: 5rem 0;

  .section-title {
    text-align: center;
    font-size: 2.5rem;
    margin-bottom: 3rem;
    color: ${({ theme }) => theme.colors.dark};
  }

  .certifications-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 2rem;
    padding: 0 1rem;
  }

  .certification-card {
    background: white;
    border-radius: 10px;
    overflow: hidden;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    transition: transform 0.3s ease, box-shadow 0.3s ease;
    display: flex;
    flex-direction: column;

    &:hover {
      transform: translateY(-5px);
      box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
    }
  }

  .certification-icon {
    background: ${({ theme }) => theme.colors.primary};
    color: white;
    padding: 1.5rem;
    text-align: center;
    font-size: 2rem;
  }

  .certification-content {
    padding: 1.5rem;
    flex-grow: 1;
    display: flex;
    flex-direction: column;

    h3 {
      font-size: 1.25rem;
      margin-bottom: 0.75rem;
      color: ${({ theme }) => theme.colors.dark};
    }
  }

  .certification-meta {
    display: flex;
    justify-content: space-between;
    color: ${({ theme }) => theme.colors.gray};
    font-size: 0.9rem;
    margin-bottom: 1rem;
  }

  .credential-id {
    font-size: 0.8rem;
    color: ${({ theme }) => theme.colors.gray};
    margin-bottom: 1rem;
    font-family: ${({ theme }) => theme.fonts.secondary};
  }

  .skills {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-bottom: 1.5rem;
  }

  .skill-tag {
    background: ${({ theme }) => `${theme.colors.primary}15`};
    color: ${({ theme }) => theme.colors.primary};
    padding: 0.25rem 0.75rem;
    border-radius: 20px;
    font-size: 0.8rem;
    font-weight: 500;
  }

  .view-credential {
    display: inline-flex;
    align-items: center;
    color: ${({ theme }) => theme.colors.primary};
    text-decoration: none;
    font-weight: 500;
    margin-top: auto;
    transition: color 0.2s ease;

    &:hover {
      color: ${({ theme }) => theme.colors.secondary};
      text-decoration: underline;
    }

    .external-icon {
      margin-left: 0.5rem;
      font-size: 0.8rem;
    }
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    .certifications-grid {
      grid-template-columns: 1fr;
      max-width: 500px;
      margin: 0 auto;
    }
  }
`;

export default Certifications;
